import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { checkEmailConfig, loadEmailConfig } from '../controllers/emailController';
import { fetchWishlistData } from '../controllers/wishlistController';
import { sendEmailToWishlist } from '../controllers/emailController';
import { getWishlistEntries } from '../models/wishlistModel';

const EmailCampaignView = () => {
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  // Removed emailFormat state as we only support plain text now
  const [playStoreLink, setPlayStoreLink] = useState(''); // New field for Play Store link
  const [appStoreLink, setAppStoreLink] = useState(''); // New field for App Store link
  const [wishlistEntries, setWishlistEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isEmailConfigured, setIsEmailConfigured] = useState(true);
  
  useEffect(() => {
    // Check email configuration
    loadEmailConfig(setIsEmailConfigured);
    
    // Load wishlist data
    loadWishlistData();
  }, []);
  
  const loadWishlistData = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const entries = await getWishlistEntries();
      // Filter out entries that already have emails sent
      const unsentEntries = entries.filter(entry => !entry.emailSent);
      setWishlistEntries(unsentEntries);
    } catch (err) {
      console.error('Error loading wishlist data:', err);
      setError('Failed to load wishlist data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSendEmail = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    
    if (!subject) {
      setError('Please enter an email subject');
      return;
    }
    
    if (!content) {
      setError('Please enter email content');
      return;
    }
    
    if (wishlistEntries.length === 0) {
      setError('No unsent wishlist subscribers found. All subscribers have already received emails.');
      return;
    }
    
    try {
      // Prepare email data with app store links (plain text only)
      const emailData = {
        subject,
        content,
        format: 'text',
        textContent: content,
        playStoreLink,
        appStoreLink
      };
      
      // Call the email controller
      const result = await sendEmailToWishlist(wishlistEntries, emailData, setIsSending, setError);
      
      if (result.success) {
        setSuccessMessage(result.data.message);
        
        // Show warning if Firebase update failed
        if (result.data.warning) {
          console.warn('Warning:', result.data.warning);
        }
        
        // Reload wishlist data to show updated email status
        loadWishlistData();
        
        // Reset form
        setSubject('');
        setContent('');
        setPlayStoreLink('');
        setAppStoreLink('');
      } else {
        setError(result.error || 'Failed to send email');
      }
    } catch (err) {
      console.error('Email send error:', err);
      setError(err.message || 'Failed to send email');
    }
  };
  
  return (
    <div>
      <h2 className="text-2xl font-semibold text-white mb-6">Email Campaign</h2>
      
      {!isEmailConfigured && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-yellow-500/20 border border-yellow-500 text-yellow-100 px-4 py-3 rounded-lg mb-6"
        >
          Email service is not configured. Please ensure you are logged in with proper admin credentials.
        </motion.div>
      )}
      
      {error && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-red-500/20 border border-red-500 text-red-100 px-4 py-3 rounded-lg mb-6"
        >
          {error}
        </motion.div>
      )}
      
      {successMessage && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-green-500/20 border border-green-500 text-green-100 px-4 py-3 rounded-lg mb-6"
        >
          {successMessage}
        </motion.div>
      )}
      
      <div className="bg-zinc-800 rounded-xl p-6 mb-8">
        <h3 className="text-xl font-medium text-white mb-4">Send Email to Wishlist Subscribers</h3>
        
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="bg-zinc-700 px-3 py-1 rounded-lg">
              <span className="text-gray-300 text-sm">Unsent Recipients: </span>
              <span className="text-white font-semibold">{isLoading ? '...' : wishlistEntries.length} subscribers</span>
            </div>
            
            <motion.button
              onClick={loadWishlistData}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="text-gray-400 hover:text-white transition-colors text-sm"
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Refresh'}
            </motion.button>
          </div>
          
          {wishlistEntries.length === 0 && !isLoading && (
            <div className="text-yellow-400 text-sm bg-yellow-500/10 px-3 py-1 rounded-lg">
              All subscribers have already received emails
            </div>
          )}
        </div>
        
        <form onSubmit={handleSendEmail} className="space-y-4">
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-1">
              Email Subject <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject"
              className="w-full px-4 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-white focus:outline-none"
              required
            />
          </div>
          
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-300 mb-1">
              Email Content (Plain Text) <span className="text-red-400">*</span>
            </label>
            
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter your plain text email content here..."
              className="w-full px-4 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-white focus:outline-none min-h-[200px]"
              required
            />
            
            <p className="text-xs text-gray-400 mt-1">
              Plain text format will be sent without any formatting for better deliverability.
            </p>
          </div>
          
          <div>
            <label htmlFor="playStoreLink" className="block text-sm font-medium text-gray-300 mb-1">
              Play Store Link
            </label>
            <input
              type="text"
              id="playStoreLink"
              value={playStoreLink}
              onChange={(e) => setPlayStoreLink(e.target.value)}
              placeholder="Enter Play Store link"
              className="w-full px-4 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-white focus:outline-none"
            />
          </div>
          
          <div>
            <label htmlFor="appStoreLink" className="block text-sm font-medium text-gray-300 mb-1">
              App Store Link
            </label>
            <input
              type="text"
              id="appStoreLink"
              value={appStoreLink}
              onChange={(e) => setAppStoreLink(e.target.value)}
              placeholder="Enter App Store link"
              className="w-full px-4 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-white focus:outline-none"
            />
          </div>
          
          <motion.button
            type="submit"
            className="px-6 py-3 bg-white text-black font-medium rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:hover:bg-white"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={isSending || wishlistEntries.length === 0 || !isEmailConfigured}
          >
            {isSending ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </span>
            ) : `Send Email to ${isLoading ? '...' : wishlistEntries.length} Subscribers`}
          </motion.button>
        </form>
      </div>
      
      <div className="bg-zinc-800 rounded-xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-medium text-white">Email Recipients (Unsent Only)</h3>
          <span className="text-gray-400 text-sm">
            {isLoading ? '...' : wishlistEntries.length} recipients ready to receive email
          </span>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center py-8">
            <svg className="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : wishlistEntries.length === 0 ? (
          <p className="text-gray-400 text-center py-4">No unsent wishlist subscribers found. All subscribers have already received emails.</p>
        ) : (
          <div className="bg-zinc-700 rounded-lg p-4 max-h-60 overflow-y-auto">
            <ul className="space-y-2">
              {wishlistEntries.map((entry) => (
                <li key={entry.id} className="flex items-center justify-between text-sm">
                  <span className="text-gray-300">
                    {entry.email} {entry.fullName ? `(${entry.fullName})` : ''}
                  </span>
                  {entry.emailSent && (
                    <div className="flex items-center space-x-2">
                      <span className="text-green-400 text-xs bg-green-500/20 px-2 py-1 rounded">
                        Sent
                      </span>
                      {entry.emailSentAt && (
                        <span className="text-gray-400 text-xs">
                          {new Date(entry.emailSentAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailCampaignView; 